import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { modelsForProvider, defaultModelFor } from "../../helpers/llmModels";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 760 },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  help: { color: theme.palette.text.secondary, fontSize: 12, marginBottom: theme.spacing(1) },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  groupList: { maxHeight: 320, overflowY: "auto", marginTop: theme.spacing(1) },
  actions: { marginTop: theme.spacing(3) },
  warnBox: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    background: theme.palette.type === "dark" ? "#3a2e12" : "#fffbeb",
    borderLeft: "4px solid #f59e0b",
    padding: "8px 12px",
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  warnText: { fontSize: 12, color: theme.palette.type === "dark" ? "#f6d58a" : "#92400e" },
  roField: {
    marginBottom: theme.spacing(2),
    fontSize: 14,
    "& strong": { fontWeight: 700 },
  },
}));

const MatchPropAdmin = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [groups, setGroups] = useState([]);
  const [tol, setTol] = useState({
    dormsTolerancePlus: 1,
    dormsToleranceMinus: 1,
    precioMargin: 10,
    topN: 5,
    minTopLevel: "PARECIDO",
  });
  const [saving, setSaving] = useState(false);

  // Modelo B: IA (admin gestiona) + fuente (read-only, la elige superadmin).
  const [llm, setLlm] = useState({ llmProvider: "openai", llmModel: "", llmKeySet: false });
  const [crm, setCrm] = useState({ inventorySourceType: "", gvaId: "", crmKeySet: false });
  const [llmKey, setLlmKey] = useState(""); // editable (vacío = no cambiar)
  const [crmKey, setCrmKey] = useState("");
  const [revealed, setRevealed] = useState({ llm: null, crm: null });
  const [revealing, setRevealing] = useState({ llm: false, crm: false });
  const [savingLlm, setSavingLlm] = useState(false);
  const [savingCrm, setSavingCrm] = useState(false);
  // Modal confirmación consumo IA al activar un grupo.
  const [confirmGroup, setConfirmGroup] = useState(null); // {groupJid, name}

  const t = (k, opts) => i18n.t(`matchpropAdmin.${k}`, opts);

  const CRM_LABELS = { tokko: "Tokko", gva: "GVA" };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matchprop/admin/config");
      setEnabled(!!data.enabled);
      setGroups(data.groups || []);
      const m = data.tolerances || {};
      setTol({
        dormsTolerancePlus: m.dormsTolerancePlus ?? 1,
        dormsToleranceMinus: m.dormsToleranceMinus ?? 1,
        // backend stores fraction; show as percentage
        precioMargin: Math.round((m.precioMargin ?? 0.1) * 100),
        topN: m.topN ?? 5,
        minTopLevel: m.minTopLevel === "EXACTO" ? "EXACTO" : "PARECIDO",
      });
      setLlm({
        llmProvider: data.llmProvider || "openai",
        llmModel: data.llmModel || "",
        llmKeySet: !!data.llmKeySet,
      });
      setCrm({
        inventorySourceType: data.inventorySourceType || "",
        gvaId: data.gvaId || "",
        crmKeySet: !!data.crmKeySet,
      });
      setLlmKey("");
      setCrmKey("");
      setRevealed({ llm: null, crm: null });
      setRevealing({ llm: false, crm: false });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Activar consume tokens IA por cada mensaje → pedir confirmación. Desactivar
  // es directo.
  const requestToggleGroup = (group, value) => {
    if (value) {
      setConfirmGroup(group);
    } else {
      doToggleGroup(group.groupJid, false);
    }
  };

  const doToggleGroup = async (groupJid, value) => {
    setGroups((gs) =>
      gs.map((g) => (g.groupJid === groupJid ? { ...g, monitored: value } : g))
    );
    try {
      await api.post(
        `/matchprop/admin/groups/${encodeURIComponent(groupJid)}/toggle`,
        { enabled: value }
      );
      toast.success(value ? t("groupOn") : t("groupOff"));
    } catch (err) {
      setGroups((gs) =>
        gs.map((g) => (g.groupJid === groupJid ? { ...g, monitored: !value } : g))
      );
      toastError(err);
    }
  };

  const confirmEnableGroup = async () => {
    const g = confirmGroup;
    setConfirmGroup(null);
    if (g) await doToggleGroup(g.groupJid, true);
  };

  const toggleReveal = async (which) => {
    if (revealing[which]) {
      setRevealing((r) => ({ ...r, [which]: false }));
      setRevealed((v) => ({ ...v, [which]: null }));
      return;
    }
    try {
      const { data } = await api.get("/matchprop/admin/reveal-key", {
        params: { which },
      });
      setRevealed((v) => ({ ...v, [which]: data.key || "" }));
      setRevealing((r) => ({ ...r, [which]: true }));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveLlm = async () => {
    setSavingLlm(true);
    try {
      const payload = { llmProvider: llm.llmProvider, llmModel: llm.llmModel };
      if (llmKey.trim()) payload.llmKey = llmKey.trim();
      const { data } = await api.put("/matchprop/admin/llm", payload);
      setLlm({ llmProvider: data.llmProvider, llmModel: data.llmModel, llmKeySet: !!data.llmKeySet });
      setLlmKey("");
      setRevealed((v) => ({ ...v, llm: null }));
      setRevealing((r) => ({ ...r, llm: false }));
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingLlm(false);
  };

  const handleSaveCrmKey = async () => {
    setSavingCrm(true);
    try {
      const payload = {};
      if (crmKey.trim()) payload.crmKey = crmKey.trim();
      if (crm.inventorySourceType === "gva") payload.gvaId = (crm.gvaId || "").trim();
      const { data } = await api.put("/matchprop/admin/crm-key", payload);
      setCrm({
        inventorySourceType: data.inventorySourceType || "",
        gvaId: data.gvaId || "",
        crmKeySet: !!data.crmKeySet,
      });
      setCrmKey("");
      setRevealed((v) => ({ ...v, crm: null }));
      setRevealing((r) => ({ ...r, crm: false }));
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingCrm(false);
  };

  const keyPlaceholder = (isSet) => (isSet ? t("keyConfigured") : t("keyNotSet"));

  const renderKeyField = (label, which, editVal, setEdit, isSet) => {
    const isRevealing = revealing[which];
    return (
      <TextField
        label={label}
        type={isRevealing ? "text" : "password"}
        variant="outlined"
        fullWidth
        size="small"
        className={classes.field}
        value={isRevealing ? (revealed[which] || "") : editVal}
        onChange={(e) => !isRevealing && setEdit(e.target.value)}
        placeholder={keyPlaceholder(isSet)}
        autoComplete="new-password"
        InputProps={{
          readOnly: isRevealing,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                title={isRevealing ? t("hideKey") : t("showKey")}
                onClick={() => toggleReveal(which)}
                disabled={!isSet}
              >
                {isRevealing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const handleSaveTolerances = async () => {
    setSaving(true);
    try {
      await api.put("/matchprop/admin/tolerances", {
        dormsTolerancePlus: tol.dormsTolerancePlus,
        dormsToleranceMinus: tol.dormsToleranceMinus,
        precioMargin: tol.precioMargin, // percentage; backend normalizes
        topN: tol.topN,
        minTopLevel: tol.minTopLevel,
      });
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const stField = (key) => (e) => setTol((p) => ({ ...p, [key]: e.target.value }));

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className={classes.root}>
        <Typography variant="h5">{t("title")}</Typography>
        <Typography className={classes.help} style={{ marginTop: 16 }}>
          {t("notEnabled")}
        </Typography>
      </div>
    );
  }

  const crmName = crm.inventorySourceType
    ? (CRM_LABELS[crm.inventorySourceType] || crm.inventorySourceType)
    : "";

  return (
    <div className={classes.root}>
      <Typography variant="h5">{t("title")}</Typography>

      {/* Inteligencia Artificial — el admin gestiona su propia key (su gasto) */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("aiSection")}</Typography>
        <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
          <InputLabel>{t("llmProvider")}</InputLabel>
          <Select
            value={llm.llmProvider}
            onChange={(e) => {
              const provider = e.target.value;
              setLlm((p) => {
                // si el modelo actual no pertenece al nuevo proveedor → default.
                const models = modelsForProvider(provider);
                const llmModel = models.includes(p.llmModel) ? p.llmModel : defaultModelFor(provider);
                return { ...p, llmProvider: provider, llmModel };
              });
            }}
            label={t("llmProvider")}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="anthropic">Anthropic</MenuItem>
            <MenuItem value="gemini">Gemini</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
          <InputLabel>{t("llmModel")}</InputLabel>
          <Select
            value={llm.llmModel || ""}
            onChange={(e) => setLlm((p) => ({ ...p, llmModel: e.target.value }))}
            label={t("llmModel")}
          >
            {/* valor guardado fuera del catálogo → mostrarlo igual, no perderlo */}
            {llm.llmModel && !modelsForProvider(llm.llmProvider).includes(llm.llmModel) && (
              <MenuItem value={llm.llmModel}>{llm.llmModel}</MenuItem>
            )}
            {modelsForProvider(llm.llmProvider).map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {renderKeyField(t("llmKey"), "llm", llmKey, setLlmKey, llm.llmKeySet)}
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={savingLlm ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={savingLlm}
            onClick={handleSaveLlm}
          >
            {t("save")}
          </Button>
        </div>
      </Paper>

      {/* Fuente de inventario — la elige superadmin (read-only); admin carga su key */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("sourceSection")}</Typography>
        {!crm.inventorySourceType ? (
          <Typography className={classes.help}>{t("sourceNotSet")}</Typography>
        ) : (
          <>
            <Typography className={classes.roField}>
              {t("sourceLabel")}: <strong>{crmName}</strong>
            </Typography>
            {crm.inventorySourceType === "gva" ? (
              <>
                <TextField
                  label={t("gvaId")}
                  variant="outlined"
                  fullWidth
                  size="small"
                  className={classes.field}
                  value={crm.gvaId || ""}
                  onChange={(e) => setCrm((c) => ({ ...c, gvaId: e.target.value }))}
                />
                <div className={classes.help}>{t("gvaHelp")}</div>
                {/* GVA token shares config.encryptedKey → which="crm" */}
                {renderKeyField(t("gvaToken"), "crm", crmKey, setCrmKey, crm.crmKeySet)}
              </>
            ) : (
              renderKeyField(t("crmKey"), "crm", crmKey, setCrmKey, crm.crmKeySet)
            )}
            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                startIcon={savingCrm ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={savingCrm}
                onClick={handleSaveCrmKey}
              >
                {t("save")}
              </Button>
            </div>
          </>
        )}
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("groupsSection")}</Typography>
        <Typography className={classes.help}>{t("groupsHelp")}</Typography>
        <div className={classes.warnBox}>
          <WarningIcon style={{ color: "#f59e0b", fontSize: 18 }} />
          <span className={classes.warnText}>{t("consumptionWarning")}</span>
        </div>
        {groups.length === 0 ? (
          <Typography className={classes.help}>{t("noGroups")}</Typography>
        ) : (
          <List className={classes.groupList} dense>
            {groups.map((g) => (
              <ListItem key={g.groupJid} divider>
                <ListItemText primary={g.name || g.groupJid} />
                <Switch
                  edge="end"
                  color="primary"
                  checked={!!g.monitored}
                  onChange={(e) => requestToggleGroup(g, e.target.checked)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>{t("tolerancesSection")}</Typography>

        <TextField
          type="number"
          label={t("dormsPlus")}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={tol.dormsTolerancePlus}
          onChange={stField("dormsTolerancePlus")}
          helperText={t("dormsPlusHelp")}
          inputProps={{ min: 0, max: 5 }}
        />
        <TextField
          type="number"
          label={t("dormsMinus")}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={tol.dormsToleranceMinus}
          onChange={stField("dormsToleranceMinus")}
          helperText={t("dormsMinusHelp")}
          inputProps={{ min: 0, max: 5 }}
        />
        <TextField
          type="number"
          label={t("precioMargin")}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={tol.precioMargin}
          onChange={stField("precioMargin")}
          helperText={t("precioMarginHelp")}
          inputProps={{ min: 0, max: 100 }}
        />
        <TextField
          type="number"
          label={t("topN")}
          variant="outlined"
          fullWidth
          size="small"
          className={classes.field}
          value={tol.topN}
          onChange={stField("topN")}
          helperText={t("topNHelp")}
          inputProps={{ min: 1, max: 20 }}
        />
        <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
          <InputLabel>{t("minTopLevel")}</InputLabel>
          <Select
            value={tol.minTopLevel}
            onChange={stField("minTopLevel")}
            label={t("minTopLevel")}
          >
            <MenuItem value="EXACTO">{t("levelExact")}</MenuItem>
            <MenuItem value="PARECIDO">{t("levelSimilar")}</MenuItem>
          </Select>
        </FormControl>
        <Typography className={classes.help}>{t("minTopLevelHelp")}</Typography>

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSaveTolerances}
          >
            {t("save")}
          </Button>
        </div>
      </Paper>

      <Dialog open={!!confirmGroup} onClose={() => setConfirmGroup(null)}>
        <DialogTitle>{t("confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("confirmBody", { group: confirmGroup?.name || "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGroup(null)} color="default">
            {t("cancel")}
          </Button>
          <Button onClick={confirmEnableGroup} color="primary" variant="contained">
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MatchPropAdmin;
